import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { User, Phone, Mail, Calendar, Users, CheckCircle, ChevronRight, LogIn, X, Lock } from 'lucide-react';
import { useAppConfig } from '../context/AppConfigContext';

import { getBackendUrl } from '../services/origin';
import CookieConsentBanner from '../components/CookieConsentBanner';
import CookieFooterLink from '../components/CookieFooterLink';
import { loadTracking } from '../services/tracking';
const API = getBackendUrl();

const DEFAULT_BRANDING = {
  primary_color: '#D44E20',
  secondary_color: '#3ECF8E',
  background_color: '#111114',
  text_color: '#FFFFFF',
  text_secondary_color: '#888888',
  logo_url: null,
  registration_hero_text: '',
  registration_subtitle: '',
  privacy_policy_url: null,
  privacy_policy_file_url: null,
};

export default function PublicRegister() {
  const { businessId: businessIdParam } = useParams();
  const appCfg = useAppConfig();
  const navigate = useNavigate();
  // In single-tenant mode this page can also be used at "/" (root landing):
  // fall back to the brand id exposed by `/api/public/app-config`.
  const businessId = businessIdParam || appCfg.brand_id || '';
  const [brand, setBrand] = useState(DEFAULT_BRANDING);
  const [businessName, setBusinessName] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', birthday: '', email: '', referral_phone: '', privacy_accepted: false, acquisition_channel: '' });
  const [errors, setErrors] = useState({});
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [thirdPartyConsent, setThirdPartyConsent] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginForm, setLoginForm] = useState({ phone: '', pin: '' });
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoverySent, setRecoverySent] = useState(false);
  const [recoverySubmitting, setRecoverySubmitting] = useState(false);

  useEffect(() => {
    // Wait until app-config has loaded before deciding we have nothing to fetch.
    if (!businessIdParam && !appCfg.loaded) return;
    if (!businessId) {
      setLoading(false);
      setError('Pagina non trovata');
      return;
    }
    axios.get(`${API}/api/public/register/${businessId}`).then(r => {
      setBrand({ ...DEFAULT_BRANDING, ...(r.data.branding || {}) });
      setBusinessName(r.data.business_name || '');
      loadTracking(businessId);
    }).catch(() => {
      setError('Pagina non trovata');
    }).finally(() => setLoading(false));
  }, [businessId, businessIdParam, appCfg.loaded]);

  const handleLoginSubmit = async () => {
    setLoginError(null);
    if (loginForm.phone.replace(/\D/g, '').length < 7) {
      setLoginError('Inserisci un numero valido');
      return;
    }
    if (loginForm.pin.length !== 4) {
      setLoginError('Il PIN è composto da 4 cifre');
      return;
    }
    setLoginSubmitting(true);
    try {
      const body = { phone: loginForm.phone.trim(), pin: loginForm.pin.trim() };
      if (businessId) body.brand_id = businessId;
      const res = await axios.post(`${API}/api/public/customer-lookup`, body);
      const tok = res.data.dashboard_token;
      const pin = loginForm.pin.trim();
      navigate(`/my/${tok}?pin=${pin}`, { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Numero o PIN non corretti';
      setLoginError(msg);
    } finally {
      setLoginSubmitting(false);
    }
  };

  const handleRecoverySubmit = async () => {
    setLoginError(null);
    if (loginForm.phone.replace(/\D/g, '').length < 7) {
      setLoginError('Inserisci un numero valido');
      return;
    }
    setRecoverySubmitting(true);
    try {
      const body = { phone: loginForm.phone.trim() };
      if (businessId) body.brand_id = businessId;
      await axios.post(`${API}/api/public/customer-recovery`, body);
      setRecoverySent(true);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Errore. Riprova fra qualche minuto.';
      setLoginError(msg);
    } finally {
      setRecoverySubmitting(false);
    }
  };

  const closeLoginModal = () => {
    setLoginOpen(false);
    setRecoveryMode(false);
    setRecoverySent(false);
    setLoginError(null);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Nome obbligatorio';
    if (form.phone.length < 9) e.phone = 'Inserisci un numero valido';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const body = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        birthday: form.birthday || null,
        email: form.email.trim() || null,
        referral_phone: form.referral_phone.trim() || null,
        acquisition_channel: form.acquisition_channel || null,
        marketing_consent: marketingConsent,
        third_party_consent: thirdPartyConsent,
      };
      const res = await axios.post(`${API}/api/public/register/${businessId}`, body);
      // Unified post-registration flow:
      //  • SMS with the PIN was already dispatched server-side.
      //  • The first dashboard access is automatic — we navigate
      //    straight to /my/<token>?pin=<pin>. The dashboard verifies,
      //    persists the PIN in localStorage and STRIPS it from the URL
      //    (see CustomerDashboard.handleVerifyWithPin), so the customer
      //    lands on their bacheca without typing anything.
      //  • Future visits via the SMS link (which carries no PIN) will
      //    require the PIN unless localStorage still holds it.
      const tok = res.data.dashboard_token;
      const pin = res.data.dashboard_pin;
      if (tok && pin) {
        navigate(`/my/${tok}?pin=${pin}`, { replace: true });
        return;
      }
      // Defensive fallback: if for any reason the response is missing
      // those fields, fall back to the legacy success screen.
      setSuccess(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Errore durante la registrazione';
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: brand.background_color }}>
      <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: brand.primary_color }} />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#111114' }}>
      <div className="text-center animate-fade-in-up">
        <span className="text-5xl block mb-4">😔</span>
        <h1 className="text-white text-xl font-bold mb-2">Pagina non trovata</h1>
        <p className="text-[#888] text-sm">Il link non e' valido.</p>
      </div>
    </div>
  );

  if (success) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: brand.background_color }}>
      <Toaster position="top-center" toastOptions={{ style: { fontFamily: 'DM Sans, sans-serif' } }} />
      <div className="text-center animate-fade-in-up max-w-sm w-full">
        <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${brand.secondary_color}20` }}>
          <CheckCircle size={40} style={{ color: brand.secondary_color }} />
        </div>
        <h1 className="text-2xl font-bold mb-2" style={{ color: brand.text_color || '#FFFFFF' }}>Registrazione completata!</h1>
        <p className="text-base mb-6" style={{ color: brand.text_secondary_color || '#888888' }}>{success.message}</p>
        <div className="rounded-2xl p-6 border border-white/10 bg-white/5 mb-6">
          <div className="text-4xl font-bold mb-1" style={{ color: brand.secondary_color }}>+{success.welcome_points}</div>
          <p className="text-sm" style={{ color: brand.text_secondary_color || '#888888' }}>punti benvenuto</p>
        </div>
        {success.dashboard_url && (
          <a
            href={success.dashboard_url}
            data-testid="go-to-dashboard-link"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold text-sm transition-all active:scale-95"
            style={{ backgroundColor: brand.primary_color }}
          >
            Vedi la tua bacheca <ChevronRight size={16} />
          </a>
        )}
      </div>
    </div>
  );

  const heroText = brand.registration_hero_text || `Unisciti al programma fedelta di ${businessName}!`;
  const tc = brand.text_color || '#FFFFFF';
  const ts = brand.text_secondary_color || '#888888';
  // Always link to the in-app /privacy-policy page so the URL is
  // deploy-agnostic (no broken external links if the admin moves the
  // policy or the brand domain changes). The admin-uploaded PDF /
  // external URL are still surfaced from inside that page.
  const privacyLink = '/privacy-policy';

  return (
    <div className="min-h-screen" style={{ backgroundColor: brand.background_color }} data-testid="public-register-page">
      <Toaster position="top-center" toastOptions={{ style: { fontFamily: 'DM Sans, sans-serif' } }} />
      <CookieConsentBanner brandId={businessId} />

      {/* Hero */}
      <div className="px-6 pt-10 pb-6 text-center animate-fade-in-up">
        {brand.logo_url && (
          <img src={brand.logo_url} alt="" className="w-16 h-16 rounded-2xl object-contain mx-auto mb-4 shadow-lg" />
        )}
        <h1 className="text-2xl font-bold mb-2" style={{ color: tc }} data-testid="hero-text">{heroText}</h1>
        <p className="text-sm" style={{ color: ts }}>{brand.registration_subtitle || 'Registrati e inizia a raccogliere punti'}</p>

        {/* Existing customer login CTA */}
        <button
          type="button"
          onClick={() => { setLoginOpen(true); setLoginError(null); }}
          data-testid="open-customer-login"
          className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium border transition-all active:scale-95"
          style={{ color: tc, borderColor: `${tc}25`, backgroundColor: `${tc}08` }}
        >
          <LogIn size={14} />
          Sei già iscritto? Accedi alla tua bacheca
        </button>
      </div>

      {/* Login modal */}
      {loginOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 animate-fade-in"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={closeLoginModal}
          data-testid="customer-login-modal"
        >
          <div
            className="w-full max-w-sm rounded-3xl p-6 border animate-fade-in-up"
            style={{ backgroundColor: brand.background_color, borderColor: `${tc}15` }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold" style={{ color: tc }}>
                {recoveryMode ? 'Recupera PIN' : 'Accedi alla bacheca'}
              </h2>
              <button
                type="button"
                onClick={closeLoginModal}
                data-testid="close-customer-login"
                className="p-1.5 rounded-lg active:scale-95"
                style={{ color: tc }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Recovery success state */}
            {recoveryMode && recoverySent ? (
              <div className="text-center py-4" data-testid="customer-recovery-success">
                <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: `${brand.secondary_color}20` }}>
                  <CheckCircle size={28} style={{ color: brand.secondary_color }} />
                </div>
                <p className="text-sm font-semibold mb-2" style={{ color: tc }}>Controlla l'SMS</p>
                <p className="text-xs leading-relaxed mb-5" style={{ color: ts }}>
                  Se il numero è registrato, riceverai entro 1 minuto un SMS con il tuo nuovo PIN e il link diretto alla tua bacheca.
                </p>
                <button
                  type="button"
                  onClick={closeLoginModal}
                  data-testid="customer-recovery-close"
                  className="w-full h-12 rounded-xl text-white font-semibold text-sm active:scale-95"
                  style={{ backgroundColor: brand.primary_color }}
                >
                  Ho capito
                </button>
              </div>
            ) : (
              <>
                <p className="text-xs mb-4" style={{ color: ts }}>
                  {recoveryMode
                    ? 'Inserisci il numero di telefono con cui ti sei registrato. Ti invieremo via SMS un nuovo PIN insieme al link per accedere.'
                    : 'Inserisci il numero di telefono con cui ti sei registrato e il PIN a 4 cifre che hai ricevuto via WhatsApp.'}
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium mb-1.5 flex items-center gap-1.5" style={{ color: ts }}>
                      <Phone size={12} /> Numero di telefono
                    </label>
                    <input
                      type="tel"
                      inputMode="tel"
                      autoFocus
                      value={loginForm.phone}
                      onChange={e => setLoginForm(f => ({ ...f, phone: e.target.value.replace(/[^\d+ ]/g, '') }))}
                      data-testid="customer-login-phone"
                      placeholder="+39 333 1234567"
                      className="w-full h-12 rounded-xl px-4 bg-white/5 border border-white/10 focus:outline-none text-sm"
                      style={{ color: tc, borderColor: loginForm.phone ? brand.primary_color : undefined }}
                    />
                  </div>
                  {!recoveryMode && (
                    <div>
                      <label className="text-xs font-medium mb-1.5 flex items-center gap-1.5" style={{ color: ts }}>
                        <Lock size={12} /> PIN (4 cifre)
                      </label>
                      <input
                        type="tel"
                        inputMode="numeric"
                        maxLength={4}
                        value={loginForm.pin}
                        onChange={e => setLoginForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                        onKeyDown={e => e.key === 'Enter' && handleLoginSubmit()}
                        data-testid="customer-login-pin"
                        placeholder="••••"
                        className="w-full h-12 rounded-xl px-4 bg-white/5 border border-white/10 focus:outline-none text-sm tracking-widest text-center font-mono"
                        style={{ color: tc, borderColor: loginForm.pin.length === 4 ? brand.primary_color : undefined }}
                      />
                    </div>
                  )}
                  {loginError && (
                    <div className="rounded-xl px-3 py-2 text-xs" data-testid="customer-login-error"
                      style={{ backgroundColor: '#FF525215', color: '#FF8888' }}>
                      {loginError}
                    </div>
                  )}
                  {recoveryMode ? (
                    <button
                      type="button"
                      onClick={handleRecoverySubmit}
                      disabled={recoverySubmitting || loginForm.phone.replace(/\D/g, '').length < 7}
                      data-testid="customer-recovery-submit"
                      className="w-full h-12 rounded-xl text-white font-semibold text-sm disabled:opacity-40 active:scale-95 transition-all flex items-center justify-center gap-2"
                      style={{ backgroundColor: brand.primary_color }}
                    >
                      {recoverySubmitting ? 'Invio...' : 'Inviami il PIN via SMS'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleLoginSubmit}
                      disabled={loginSubmitting || loginForm.pin.length !== 4 || loginForm.phone.replace(/\D/g, '').length < 7}
                      data-testid="customer-login-submit"
                      className="w-full h-12 rounded-xl text-white font-semibold text-sm disabled:opacity-40 active:scale-95 transition-all flex items-center justify-center gap-2"
                      style={{ backgroundColor: brand.primary_color }}
                    >
                      {loginSubmitting ? 'Accesso...' : (<>Accedi <ChevronRight size={16} /></>)}
                    </button>
                  )}
                </div>

                {/* Toggle between login and recovery */}
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => { setRecoveryMode(m => !m); setLoginError(null); }}
                    data-testid="customer-recovery-toggle"
                    className="text-xs underline"
                    style={{ color: ts }}
                  >
                    {recoveryMode ? '← Torna all\'accesso con PIN' : 'PIN dimenticato? Ricevilo via SMS'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Form */}
      <div className="px-6 pb-10 max-w-sm mx-auto animate-fade-in-up stagger-1">
        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="text-xs font-medium mb-1.5 flex items-center gap-1.5" style={{ color: ts }}><User size={12} /> Nome e cognome *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              data-testid="register-name"
              placeholder="Mario Rossi"
              className="w-full h-12 rounded-xl px-4 bg-white/5 border border-white/10 focus:outline-none text-sm"
              style={{ color: tc, borderColor: errors.name ? '#FF5555' : form.name ? brand.primary_color : undefined }}
            />
            {errors.name && <p className="text-[#FF5555] text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Phone */}
          <div>
            <label className="text-xs font-medium mb-1.5 flex items-center gap-1.5" style={{ color: ts }}><Phone size={12} /> Numero di telefono *</label>
            <input
              type="tel"
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value.replace(/[^\d+]/g, '') }))}
              data-testid="register-phone"
              placeholder="+39 333 1234567"
              className="w-full h-12 rounded-xl px-4 bg-white/5 border border-white/10 focus:outline-none text-sm"
              style={{ color: tc, borderColor: errors.phone ? '#FF5555' : form.phone ? brand.primary_color : undefined }}
            />
            {errors.phone && <p className="text-[#FF5555] text-xs mt-1">{errors.phone}</p>}
          </div>

          {/* Birthday */}
          <div>
            <label className="text-xs font-medium mb-1.5 flex items-center gap-1.5" style={{ color: ts }}><Calendar size={12} /> Data di nascita</label>
            <input
              type="date"
              value={form.birthday}
              onChange={e => setForm(f => ({ ...f, birthday: e.target.value }))}
              data-testid="register-birthday"
              className="w-full h-12 rounded-xl px-4 bg-white/5 border border-white/10 focus:outline-none text-sm"
              style={{ color: tc, borderColor: form.birthday ? brand.primary_color : undefined, colorScheme: 'dark' }}
            />
          </div>

          {/* Email */}
          <div>
            <label className="text-xs font-medium mb-1.5 flex items-center gap-1.5" style={{ color: ts }}><Mail size={12} /> Email (facoltativa)</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              data-testid="register-email"
              placeholder="mario@email.it"
              className="w-full h-12 rounded-xl px-4 bg-white/5 border border-white/10 focus:outline-none text-sm"
              style={{ color: tc, borderColor: form.email ? brand.primary_color : undefined }}
            />
          </div>

          {/* Referral phone */}
          <div className="rounded-xl p-4 border border-white/5 bg-white/5">
            <label className="text-xs font-medium mb-1.5 flex items-center gap-1.5" style={{ color: ts }}><Users size={12} /> Presentato da un amico?</label>
            <p className="text-[10px] mb-2" style={{ color: `${ts}88` }}>Se un amico ti ha presentato, inserisci il suo numero per ricevere entrambi dei punti bonus!</p>
            <input
              type="tel"
              value={form.referral_phone}
              onChange={e => setForm(f => ({ ...f, referral_phone: e.target.value.replace(/[^\d+]/g, '') }))}
              data-testid="register-referral"
              placeholder="Numero dell'amico (facoltativo)"
              className="w-full h-12 rounded-xl px-4 bg-white/5 border border-white/10 focus:outline-none text-sm"
              style={{ color: tc, borderColor: form.referral_phone ? brand.primary_color : undefined }}
            />
          </div>

          {/* Acquisition channel */}
          <div>
            <label className="text-xs font-medium mb-1.5 flex items-center gap-1.5" style={{ color: ts }}><Users size={12} /> Come ci hai conosciuto?</label>
            <select
              value={form.acquisition_channel}
              onChange={e => setForm(f => ({ ...f, acquisition_channel: e.target.value }))}
              data-testid="register-acquisition-channel"
              className="w-full h-12 rounded-xl px-4 bg-white/5 border border-white/10 focus:outline-none text-sm appearance-none"
              style={{ color: tc, borderColor: form.acquisition_channel ? brand.primary_color : undefined }}
            >
              <option value="">Seleziona (facoltativo)</option>
              <option value="passaparola">Passaparola</option>
              <option value="social">Social media</option>
              <option value="volantino">Volantinaggio</option>
              <option value="google">Google / Ricerca online</option>
              <option value="evento">Evento</option>
              <option value="passaggio">Passaggio casuale</option>
              <option value="altro">Altro</option>
            </select>
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={submitting || !form.name.trim() || form.phone.length < 9 || !form.privacy_accepted}
            data-testid="register-submit"
            className="w-full h-14 rounded-xl text-white font-bold text-base disabled:opacity-40 active:scale-95 transition-all mt-2"
            style={{ backgroundColor: brand.primary_color }}
          >
            {submitting ? 'Registrazione...' : 'Registrati'}
          </button>

          {/* GDPR consent checkboxes */}
          <div className="space-y-3 mt-4" data-testid="privacy-checkbox-section">
            {/* 1. Profiling - mandatory (the main privacy acceptance) */}
            <label className="flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all"
              style={{ background: form.privacy_accepted ? `${brand.primary_color}10` : 'rgba(255,255,255,0.03)', borderColor: form.privacy_accepted ? brand.primary_color : 'rgba(255,255,255,0.1)' }}>
              <input
                type="checkbox"
                checked={form.privacy_accepted || false}
                onChange={e => setForm(f => ({ ...f, privacy_accepted: e.target.checked }))}
                data-testid="privacy-checkbox"
                className="mt-0.5 w-4 h-4 flex-shrink-0"
                style={{ accentColor: brand.primary_color }}
              />
              <div>
                <span className="text-xs font-medium" style={{ color: tc }}>Programma fedelta e profilazione</span>
                <span className="text-[#FF5555] text-xs ml-1">*</span>
                <p className="text-[10px] mt-0.5 leading-relaxed" style={{ color: ts }}>
                  Ho letto l'<a href={privacyLink} target="_blank" rel="noopener noreferrer" data-testid="privacy-link" className="underline font-medium" style={{ color: brand.primary_color }}>informativa privacy</a> e acconsento al trattamento dei dati per la partecipazione al programma, inclusa profilazione e analisi comportamentale. <span className="inline-flex align-middle ml-1" style={{ color: ts }}><CookieFooterLink /></span>
                </p>
              </div>
            </label>

            {/* 2. Marketing */}
            <label className="flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all"
              style={{ background: marketingConsent ? `${brand.primary_color}10` : 'rgba(255,255,255,0.03)', borderColor: marketingConsent ? brand.primary_color : 'rgba(255,255,255,0.1)' }}>
              <input
                type="checkbox"
                checked={marketingConsent}
                onChange={e => setMarketingConsent(e.target.checked)}
                data-testid="consent-marketing-checkbox"
                className="mt-0.5 w-4 h-4 flex-shrink-0"
                style={{ accentColor: brand.primary_color }}
              />
              <div>
                <span className="text-xs font-medium" style={{ color: tc }}>Comunicazioni di marketing</span>
                <p className="text-[10px] mt-0.5" style={{ color: ts }}>Acconsento a ricevere comunicazioni promozionali tramite WhatsApp, email e SMS.</p>
                {!marketingConsent && (
                  <div className="mt-1.5 p-2 bg-[#FF555520] rounded-lg border border-[#FF555540]">
                    <p className="text-[#FF8888] text-[10px] leading-relaxed">
                      Senza questo consenso non potrai ricevere notifiche su punti, premi sbloccati, promozioni e tutti i vantaggi del programma. Ti consigliamo di accettare.
                    </p>
                  </div>
                )}
              </div>
            </label>

            {/* 3. Third party */}
            <label className="flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all"
              style={{ background: thirdPartyConsent ? `${brand.primary_color}10` : 'rgba(255,255,255,0.03)', borderColor: thirdPartyConsent ? brand.primary_color : 'rgba(255,255,255,0.1)' }}>
              <input
                type="checkbox"
                checked={thirdPartyConsent}
                onChange={e => setThirdPartyConsent(e.target.checked)}
                data-testid="consent-thirdparty-checkbox"
                className="mt-0.5 w-4 h-4 flex-shrink-0"
                style={{ accentColor: brand.primary_color }}
              />
              <div>
                <span className="text-xs font-medium" style={{ color: tc }}>Condivisione con piattaforme terze</span>
                <span className="text-[10px] ml-1" style={{ color: ts }}>(opzionale)</span>
                <p className="text-[10px] mt-0.5" style={{ color: ts }}>Acconsento alla condivisione dei miei dati con Meta, Google e altre piattaforme per pubblicita personalizzata.</p>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center pb-6">
        {brand.logo_url && <img src={brand.logo_url} alt="" className="w-6 h-6 rounded object-contain mx-auto mb-1 opacity-30" />}
        <p className="text-[10px]" style={{ color: `${ts}44` }}>{businessName}</p>
      </div>
    </div>
  );
}
